/**
 * Sanskrit Studio — Versioned Conversation feed.
 *
 * X.com-style feed for capturing and versioning Sanskrit conversations.
 * Each conversation has 1–9 blocks; each block carries five mandatory fields:
 *   Sanskrit Text · Hindi Translation · Pannineeya/Yaska/Nighantu Analysis
 *   Speaker Expression · Context Details
 *
 * Conversations are stored privately on GCloud and can be shared with
 * role-holders (Upadhyaya / Acharya / Guru / Raja).
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import '../styles/SanskritStudio.css';
import { useAuth } from '../contexts/AuthContext';
import {
  listConversations,
  getConversation,
  createConversation,
  updateConversation,
  deleteConversation,
  shareConversation,
  type ConvSummary,
  type ConvFull,
  type ConvBlock,
} from '../lib/api';

// ─── Types ────────────────────────────────────────────────────────────────────

type DraftBlock = {
  id: string;
  sanskrit_text: string;
  hindi_translation: string;
  vyakarana_nirukta: string;
  speaker_expression: string;
  context: string;
};

type DraftConv = {
  title: string;
  privacy: 'private' | 'shared';
  blocks: DraftBlock[];
};

// Accepted by App.tsx — unused props are retained for type compatibility
interface Props {
  onNavigate: (path: string) => void;
  initialSeedId?: string;
  initialViewMode?: string;
  initialSegmentId?: string | number | null;
  initialTab?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function emptyBlock(): DraftBlock {
  return {
    id: `blk-${Math.random().toString(36).slice(2, 9)}`,
    sanskrit_text: '',
    hindi_translation: '',
    vyakarana_nirukta: '',
    speaker_expression: '',
    context: '',
  };
}

function emptyDraft(): DraftConv {
  return { title: '', privacy: 'private', blocks: [emptyBlock()] };
}

function relativeTime(iso: string): string {
  if (!iso) return '';
  const delta = Date.now() - new Date(iso).getTime();
  const m = Math.floor(delta / 60000);
  if (m < 1) return 'अभी';
  if (m < 60) return `${m}मि पहले`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}घं पहले`;
  return `${Math.floor(h / 24)}दि पहले`;
}

function blockLabel(field: keyof DraftBlock): string {
  const labels: Record<string, string> = {
    sanskrit_text:     'संस्कृत पाठ',
    hindi_translation: 'हिंदी अनुवाद',
    vyakarana_nirukta: 'व्याकरण / निरुक्त / निघण्टु',
    speaker_expression:'वक्ता भाव',
    context:           'प्रसंग',
  };
  return labels[field] ?? field;
}

const BLOCK_FIELDS: (keyof Omit<DraftBlock, 'id'>)[] = [
  'sanskrit_text',
  'hindi_translation',
  'vyakarana_nirukta',
  'speaker_expression',
  'context',
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function BlockEditor({
  block,
  index,
  total,
  onChange,
  onRemove,
}: {
  block: DraftBlock;
  index: number;
  total: number;
  onChange: (b: DraftBlock) => void;
  onRemove: () => void;
}) {
  return (
    <div className="sc-block-editor">
      <div className="sc-block-editor-header">
        <span className="sc-block-num">खण्ड {index + 1}</span>
        {total > 1 && (
          <button type="button" className="sc-block-remove" onClick={onRemove} title="खण्ड हटाएं">
            ✕
          </button>
        )}
      </div>
      {BLOCK_FIELDS.map(f => (
        <div className="sc-field" key={f}>
          <label className="sc-field-label">{blockLabel(f)}</label>
          <textarea
            className="sc-field-input"
            rows={f === 'sanskrit_text' ? 3 : 2}
            value={block[f]}
            onChange={e => onChange({ ...block, [f]: e.target.value })}
            placeholder={`${blockLabel(f)}…`}
          />
        </div>
      ))}
    </div>
  );
}

function Md({ children }: { children: string }) {
  return <ReactMarkdown remarkPlugins={[remarkGfm]}>{children}</ReactMarkdown>;
}

function BlockReadView({ block, index }: { block: ConvBlock; index: number }) {
  return (
    <div className="sc-block-read">
      <span className="sc-block-num-sm">खण्ड {index + 1}</span>
      <div className="sc-block-field">
        <span className="sc-field-label-sm">संस्कृत पाठ</span>
        <p className="sc-field-sa">{block.sanskrit_text}</p>
      </div>
      <div className="sc-block-field">
        <span className="sc-field-label-sm">हिंदी अनुवाद</span>
        <div className="sc-md"><Md>{block.hindi_translation}</Md></div>
      </div>
      <div className="sc-block-field">
        <span className="sc-field-label-sm">व्याकरण / निरुक्त</span>
        <div className="sc-md"><Md>{block.vyakarana_nirukta}</Md></div>
      </div>
      <div className="sc-block-field">
        <span className="sc-field-label-sm">वक्ता भाव</span>
        <div className="sc-md"><Md>{block.speaker_expression}</Md></div>
      </div>
      <div className="sc-block-field">
        <span className="sc-field-label-sm">प्रसंग</span>
        <div className="sc-md"><Md>{block.context}</Md></div>
      </div>
    </div>
  );
}

function ComposeForm({
  initial,
  onSave,
  onCancel,
  saving,
  saveError,
}: {
  initial?: DraftConv;
  onSave: (draft: DraftConv) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
  saveError: string | null;
}) {
  const [draft, setDraft] = useState<DraftConv>(initial ?? emptyDraft());

  function updateBlock(i: number, b: DraftBlock) {
    const blocks = draft.blocks.slice();
    blocks[i] = b;
    setDraft(d => ({ ...d, blocks }));
  }
  function addBlock() {
    if (draft.blocks.length >= 9) return;
    setDraft(d => ({ ...d, blocks: [...d.blocks, emptyBlock()] }));
  }
  function removeBlock(i: number) {
    if (draft.blocks.length <= 1) return;
    setDraft(d => ({ ...d, blocks: d.blocks.filter((_, idx) => idx !== i) }));
  }

  return (
    <div className="sc-compose-form">
      <div className="sc-compose-title-row">
        <input
          type="text"
          className="sc-title-input"
          placeholder="शीर्षक (ऐच्छिक) · Title (optional)"
          value={draft.title}
          onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
          maxLength={200}
        />
        <label className="sc-privacy-toggle">
          <select
            value={draft.privacy}
            onChange={e => setDraft(d => ({ ...d, privacy: e.target.value as 'private' | 'shared' }))}
            className="sc-privacy-select"
          >
            <option value="private">🔒 निजी</option>
            <option value="shared">🔗 साझा</option>
          </select>
        </label>
      </div>

      {draft.blocks.map((b, i) => (
        <BlockEditor
          key={b.id}
          block={b}
          index={i}
          total={draft.blocks.length}
          onChange={nb => updateBlock(i, nb)}
          onRemove={() => removeBlock(i)}
        />
      ))}

      {draft.blocks.length < 9 && (
        <button type="button" className="sc-add-block-btn" onClick={addBlock}>
          + खण्ड जोड़ें · Add Block
        </button>
      )}

      {saveError && <p className="sc-error">{saveError}</p>}

      <div className="sc-compose-actions">
        <button type="button" className="sc-cancel-btn" onClick={onCancel} disabled={saving}>
          रद्द करें
        </button>
        <button
          type="button"
          className="sc-save-btn"
          disabled={saving}
          onClick={() => onSave(draft)}
        >
          {saving ? '…' : 'सहेजें · Save'}
        </button>
      </div>
    </div>
  );
}

function ShareModal({
  onShare,
  onClose,
  sharing,
  shareError,
}: {
  onShare: (uid: string) => Promise<void>;
  onClose: () => void;
  sharing: boolean;
  shareError: string | null;
}) {
  const [uid, setUid] = useState('');
  return (
    <div className="sc-modal-overlay" onClick={onClose}>
      <div className="sc-modal" onClick={e => e.stopPropagation()}>
        <h3 className="sc-modal-title">संवाद साझा करें · Share Conversation</h3>
        <p className="sc-modal-desc">
          उपाध्याय / आचार्य / गुरु / राजा के Firebase UID दर्ज करें।
        </p>
        <input
          className="sc-modal-input"
          placeholder="Recipient Firebase UID"
          value={uid}
          onChange={e => setUid(e.target.value.trim())}
        />
        {shareError && <p className="sc-error">{shareError}</p>}
        <div className="sc-modal-actions">
          <button className="sc-cancel-btn" onClick={onClose} disabled={sharing}>रद्द</button>
          <button
            className="sc-save-btn"
            disabled={sharing || !uid}
            onClick={() => onShare(uid)}
          >
            {sharing ? '…' : 'साझा करें'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function SanskritStudio({ onNavigate }: Props) {
  const { user, authLoading } = useAuth();

  // Feed state
  const [conversations, setConversations]   = useState<ConvSummary[]>([]);
  const [loadError, setLoadError]           = useState<string | null>(null);
  const [feedLoading, setFeedLoading]       = useState(false);

  // Compose state
  const [composeOpen, setComposeOpen]       = useState(false);
  const [savingNew, setSavingNew]           = useState(false);
  const [saveNewError, setSaveNewError]     = useState<string | null>(null);

  // Card interaction state
  const [expandedId, setExpandedId]         = useState<string | null>(null);
  const [fullConv, setFullConv]             = useState<ConvFull | null>(null);
  const [loadingFull, setLoadingFull]       = useState(false);

  const [editingId, setEditingId]           = useState<string | null>(null);
  const [editSaving, setEditSaving]         = useState(false);
  const [editError, setEditError]           = useState<string | null>(null);

  const [appendingId, setAppendingId]       = useState<string | null>(null);
  const [appendBlock, setAppendBlock]       = useState<DraftBlock>(() => emptyBlock());
  const [appendSaving, setAppendSaving]     = useState(false);
  const [appendError, setAppendError]       = useState<string | null>(null);

  const [deletePendingId, setDeletePendingId] = useState<string | null>(null);
  const [deleting, setDeleting]             = useState(false);

  const [shareModalId, setShareModalId]     = useState<string | null>(null);
  const [sharing, setSharing]               = useState(false);
  const [shareError, setShareError]         = useState<string | null>(null);

  const composeRef = useRef<HTMLDivElement>(null);

  // ── Token helper — uses context user directly to avoid auth.currentUser null race ──
  const getToken = useCallback(async (): Promise<string | null> => {
    try {
      return (await user?.getIdToken()) ?? null;
    } catch {
      return null;
    }
  }, [user]);

  // ── Load feed ───────────────────────────────────────────────────────────────
  const loadFeed = useCallback(async () => {
    const token = await getToken();
    if (!token) {
      setLoadError('प्रमाणीकरण टोकन नहीं मिला — कृपया पृष्ठ ताज़ा करें। (Token unavailable — please refresh.)');
      return;
    }
    setFeedLoading(true);
    setLoadError(null);
    try {
      const convs = await listConversations(token);
      setConversations(convs);
    } catch (err: unknown) {
      setLoadError(err instanceof Error ? err.message : 'फ़ीड लोड विफल');
    } finally {
      setFeedLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (!authLoading && user) loadFeed();
  }, [authLoading, user, loadFeed]);

  // ── Expand card (load full blocks) ──────────────────────────────────────────
  async function handleExpand(id: string) {
    if (expandedId === id) {
      setExpandedId(null);
      setFullConv(null);
      setEditingId(null);
      setAppendingId(null);
      setAppendBlock(emptyBlock());
      return;
    }
    setExpandedId(id);
    setEditingId(null);
    setAppendingId(null);
    setAppendBlock(emptyBlock());
    setFullConv(null);
    setLoadingFull(true);
    try {
      const token = await getToken();
      if (!token) return;
      const full = await getConversation(id, token);
      setFullConv(full);
    } catch {
      setExpandedId(null);
    } finally {
      setLoadingFull(false);
    }
  }

  // ── Create new conversation ──────────────────────────────────────────────────
  async function handleCreate(draft: DraftConv) {
    setSavingNew(true);
    setSaveNewError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('प्रमाणीकरण विफल');
      const created = await createConversation(draft, token);
      setConversations(prev => [created, ...prev]);
      setComposeOpen(false);
    } catch (err: unknown) {
      setSaveNewError(err instanceof Error ? err.message : 'सहेजना विफल');
    } finally {
      setSavingNew(false);
    }
  }

  // ── Update existing conversation ────────────────────────────────────────────
  async function handleUpdate(id: string, draft: DraftConv) {
    const existing = conversations.find(c => c.id === id);
    if (!existing) return;
    setEditSaving(true);
    setEditError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('प्रमाणीकरण विफल');
      const updated = await updateConversation(id, { ...draft, client_version: existing.version }, token);
      setConversations(prev => prev.map(c => c.id === id ? updated : c));
      setEditingId(null);
      // Refresh full view
      const full = await getConversation(id, token);
      setFullConv(full);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'अद्यतन विफल';
      if (msg.includes('409') || msg.includes('conflict') || msg.toLowerCase().includes('version')) {
        setEditError('संस्करण विरोध — कृपया पृष्ठ ताज़ा करें।');
      } else {
        setEditError(msg);
      }
    } finally {
      setEditSaving(false);
    }
  }

  // ── Append a single new block to existing conversation ─────────────────────
  async function handleAppend(id: string) {
    if (!fullConv || fullConv.id !== id) return;
    if (fullConv.blocks.length >= 9) {
      setAppendError('अधिकतम 9 खण्ड हो सकते हैं।');
      return;
    }
    const existing = conversations.find(c => c.id === id);
    if (!existing) return;
    setAppendSaving(true);
    setAppendError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('प्रमाणीकरण विफल');
      const updated = await updateConversation(id, {
        title:          fullConv.title,
        privacy:        fullConv.privacy,
        blocks:         [...fullConv.blocks, appendBlock],
        client_version: existing.version,
      }, token);
      setConversations(prev => prev.map(c => c.id === id ? updated : c));
      // Refresh full view with new block included
      const full = await getConversation(id, token);
      setFullConv(full);
      setAppendingId(null);
      setAppendBlock(emptyBlock());
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'जोड़ना विफल';
      setAppendError(msg.includes('409') || msg.toLowerCase().includes('version')
        ? 'संस्करण विरोध — कृपया पृष्ठ ताज़ा करें।'
        : msg);
    } finally {
      setAppendSaving(false);
    }
  }

  // ── Delete ───────────────────────────────────────────────────────────────────
  async function handleDelete(id: string) {
    setDeleting(true);
    try {
      const token = await getToken();
      if (!token) throw new Error('प्रमाणीकरण विफल');
      await deleteConversation(id, token);
      setConversations(prev => prev.filter(c => c.id !== id));
      setDeletePendingId(null);
      setExpandedId(null);
      setFullConv(null);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'हटाना विफल');
    } finally {
      setDeleting(false);
    }
  }

  // ── Share ─────────────────────────────────────────────────────────────────
  async function handleShare(id: string, recipientUid: string) {
    setSharing(true);
    setShareError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('प्रमाणीकरण विफल');
      const updated = await shareConversation(id, recipientUid, token);
      setConversations(prev => prev.map(c => c.id === id ? updated : c));
      setShareModalId(null);
    } catch (err: unknown) {
      setShareError(err instanceof Error ? err.message : 'साझा करना विफल');
    } finally {
      setSharing(false);
    }
  }

  // ─── Auth gate ────────────────────────────────────────────────────────────
  if (authLoading) {
    return (
      <div className="sc-auth-gate">
        <span className="sc-gate-icon">✦</span>
        <p>लोड हो रहा है…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="sc-auth-gate">
        <span className="sc-gate-icon">🪷</span>
        <h2 className="sc-gate-title">संस्कृत स्टूडियो</h2>
        <p className="sc-gate-body">
          संवाद देखने के लिए साइन इन करें।<br />
          <em>Sign in to access your Sanskrit Studio.</em>
        </p>
        <button className="sc-gate-btn" onClick={() => onNavigate('/profile')}>
          साइन इन करें · Sign In
        </button>
      </div>
    );
  }

  // ─── Feed ─────────────────────────────────────────────────────────────────
  return (
    <div className="sc-root">
      <header className="sc-header">
        <h1 className="sc-header-title">🪷 संस्कृत स्टूडियो</h1>
        <button
          className="sc-compose-trigger"
          onClick={() => {
            setComposeOpen(o => !o);
            setSaveNewError(null);
            setTimeout(() => composeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
          }}
        >
          {composeOpen ? '✕ बंद करें' : '+ नई संवाद'}
        </button>
      </header>

      {/* Compose box */}
      {composeOpen && (
        <div className="sc-compose-card" ref={composeRef}>
          <ComposeForm
            onSave={handleCreate}
            onCancel={() => { setComposeOpen(false); setSaveNewError(null); }}
            saving={savingNew}
            saveError={saveNewError}
          />
        </div>
      )}

      {/* Feed */}
      {feedLoading && (
        <div className="sc-feed-loading">⏳ संवाद लोड हो रहे हैं…</div>
      )}
      {loadError && (
        <div className="sc-error sc-feed-error">
          {loadError}
          <button className="sc-retry-btn" onClick={loadFeed}>↺ पुनः प्रयास · Retry</button>
        </div>
      )}
      {!feedLoading && conversations.length === 0 && !loadError && (
        <div className="sc-empty-state">
          <span className="sc-empty-icon">🕉</span>
          <p>अभी तक कोई संवाद नहीं। ऊपर "+ नई संवाद" पर क्लिक करें।</p>
          <p><em>No conversations yet. Click "+ नई संवाद" above to begin.</em></p>
          <button className="sc-retry-btn" onClick={loadFeed}>↺ पुनः लोड करें · Reload</button>
        </div>
      )}

      <ul className="sc-feed">
        {conversations.map(conv => {
          const isExpanded = expandedId === conv.id;
          const isEditing  = editingId  === conv.id;
          return (
            <li key={conv.id} className={`sc-card ${isExpanded ? 'sc-card--expanded' : ''}`}>
              {/* Card header row */}
              <div className="sc-card-header" onClick={() => handleExpand(conv.id)}>
                <div className="sc-card-meta">
                  <span className="sc-card-title">
                    {conv.title || conv.snippet || '(अशीर्षक संवाद)'}
                  </span>
                  <span className="sc-card-badges">
                    <span className="sc-badge sc-badge--blocks">⊞ {conv.block_count}</span>
                    <span className="sc-badge sc-badge--version">v{conv.version}</span>
                    <span className={`sc-badge sc-badge--privacy ${conv.privacy === 'shared' ? 'sc-badge--shared' : ''}`}>
                      {conv.privacy === 'shared' ? '🔗' : '🔒'}
                    </span>
                  </span>
                </div>
                <span className="sc-card-time">{relativeTime(conv.updated_at)}</span>
              </div>

              {/* Snippet (collapsed) */}
              {!isExpanded && conv.snippet && (
                <p className="sc-card-snippet">{conv.snippet}</p>
              )}

              {/* Expanded content */}
              {isExpanded && (
                <div className="sc-card-body">
                  {loadingFull && !fullConv && (
                    <p className="sc-loading-inline">लोड हो रहा है…</p>
                  )}

                  {isEditing && fullConv ? (
                    <ComposeForm
                      initial={{
                        title:   fullConv.title,
                        privacy: fullConv.privacy,
                        blocks:  fullConv.blocks.map(b => ({ ...b })),
                      }}
                      onSave={draft => handleUpdate(conv.id, draft)}
                      onCancel={() => { setEditingId(null); setEditError(null); }}
                      saving={editSaving}
                      saveError={editError}
                    />
                  ) : (
                    fullConv?.id === conv.id && (
                      <div className="sc-blocks-read">
                        {fullConv.blocks.map((b, i) => (
                          <BlockReadView key={b.id} block={b} index={i} />
                        ))}
                        {/* Inline append form */}
                        {appendingId === conv.id && (
                          <div className="sc-append-form">
                            <p className="sc-append-label">नया खण्ड जोड़ें · Add Block {fullConv.blocks.length + 1}</p>
                            <BlockEditor
                              block={appendBlock}
                              index={fullConv.blocks.length}
                              total={fullConv.blocks.length + 1}
                              onChange={setAppendBlock}
                              onRemove={() => { setAppendingId(null); setAppendBlock(emptyBlock()); }}
                            />
                            {appendError && <p className="sc-error">{appendError}</p>}
                            <div className="sc-compose-actions">
                              <button className="sc-cancel-btn" onClick={() => { setAppendingId(null); setAppendBlock(emptyBlock()); setAppendError(null); }} disabled={appendSaving}>
                                रद्द
                              </button>
                              <button className="sc-save-btn" onClick={() => handleAppend(conv.id)} disabled={appendSaving}>
                                {appendSaving ? '…' : 'जोड़ें · Append'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  )}

                  {/* Action bar */}
                  {!isEditing && appendingId !== conv.id && fullConv?.id === conv.id && (
                    <div className="sc-card-actions">
                      {fullConv.blocks.length < 9 && (
                        <button
                          className="sc-action-btn sc-action-btn--append"
                          onClick={() => { setAppendingId(conv.id); setAppendBlock(emptyBlock()); setAppendError(null); }}
                        >
                          ➕ खण्ड जोड़ें
                        </button>
                      )}
                      <button
                        className="sc-action-btn"
                        onClick={() => { setEditingId(conv.id); setEditError(null); }}
                      >
                        ✏️ संपादन
                      </button>
                      <button
                        className="sc-action-btn"
                        onClick={() => { setShareModalId(conv.id); setShareError(null); }}
                      >
                        🔗 साझा
                      </button>
                      <button
                        className="sc-action-btn sc-action-btn--danger"
                        onClick={() => setDeletePendingId(conv.id)}
                      >
                        🗑 हटाएं
                      </button>
                    </div>
                  )}

                  {/* Delete confirmation */}
                  {deletePendingId === conv.id && (
                    <div className="sc-delete-confirm">
                      <p>क्या आप इस संवाद को हटाना चाहते हैं?</p>
                      <div className="sc-modal-actions">
                        <button className="sc-cancel-btn" onClick={() => setDeletePendingId(null)} disabled={deleting}>
                          रद्द
                        </button>
                        <button
                          className="sc-save-btn sc-save-btn--danger"
                          onClick={() => handleDelete(conv.id)}
                          disabled={deleting}
                        >
                          {deleting ? '…' : 'हटाएं'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {/* Share modal */}
      {shareModalId && (
        <ShareModal
          onShare={uid => handleShare(shareModalId, uid)}
          onClose={() => { setShareModalId(null); setShareError(null); }}
          sharing={sharing}
          shareError={shareError}
        />
      )}
    </div>
  );
}
